/**
 * Modern Bold · <mb-product-carousel> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * Infinite centered coverflow of curated collection siblings. The active card
 * sits DEAD-CENTRE at all times; neighbours peek + dim on both sides; prev/next
 * slide the strip one card. Looping is seamless: the full card set is cloned on
 * each side and the index is rebased into the middle period after every slide,
 * so the active is always centred with no leading/trailing empty gap and the
 * list wraps around endlessly.
 *
 * Transform-based (not native scroll): the track is translateX-ed to centre the
 * active card; per-card scale/opacity is painted by distance from centre.
 *
 * Self-contained — no dependencies on other scripts.
 *
 * DOM contract (rendered by snippets/modern-product-carousel.liquid):
 *   <mb-product-carousel>
 *     [data-mb-carousel-viewport]        overflow-clip window
 *       [data-mb-carousel-track]         the flex strip that gets translated
 *         [data-mb-carousel-card] × N    cards (each an <a> to the product)
 *     [data-mb-carousel-controls]        control bar (present only when N > 1)
 *       [data-mb-carousel-prev|next]     arrow <button>s (loop — never disabled)
 *       [data-mb-carousel-scrubber]      progress bar → jump to a card
 *         [data-mb-carousel-fill] / [data-mb-carousel-handle]
 *       [data-mb-carousel-current]       numeric current index
 */
if (!customElements.get('mb-product-carousel')) {
  const EASE_MS = 550;

  class MBProductCarousel extends HTMLElement {
    #cleanups = [];
    #viewport = null;
    #track = null;
    #cards = [];        // all cards in the DOM (middle set flanked by clones)
    #n = 0;             // real card count
    #activeAbs = 0;     // index into #cards of the centred card
    #controls = null;
    #prevBtn = null;
    #nextBtn = null;
    #scrubber = null;
    #fill = null;
    #handle = null;
    #currentEl = null;
    #reduce = false;
    #animating = false;
    #resetTimer = 0;
    #suppressClick = false;

    connectedCallback() {
      this.#viewport  = this.querySelector('[data-mb-carousel-viewport]');
      this.#track     = this.querySelector('[data-mb-carousel-track]');
      this.#controls  = this.querySelector('[data-mb-carousel-controls]');
      this.#prevBtn   = this.querySelector('[data-mb-carousel-prev]');
      this.#nextBtn   = this.querySelector('[data-mb-carousel-next]');
      this.#scrubber  = this.querySelector('[data-mb-carousel-scrubber]');
      this.#fill      = this.querySelector('[data-mb-carousel-fill]');
      this.#handle    = this.querySelector('[data-mb-carousel-handle]');
      this.#currentEl = this.querySelector('[data-mb-carousel-current]');

      // Real cards only — never count clones a prior mount may have left behind.
      const originals = Array.from(this.querySelectorAll('[data-mb-carousel-card]:not([data-clone])'));
      this.#n = originals.length;
      if (!this.#viewport || !this.#track || this.#n === 0) return;

      this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#wireTracking();

      if (this.#n === 1) {
        // Nothing to loop — centre the one card, no controls.
        this.classList.add('mb-carousel--single');
        this.#cards = originals;
        this.#activeAbs = 0;
        originals[0].classList.add('is-active');
        return;
      }

      // Mobile → native horizontal scroll-snap strip (the browser owns swipe,
      // momentum, and vertical-vs-horizontal intent, so a page scroll never
      // nudges the carousel). Desktop → transform coverflow with looping +
      // pointer drag / wheel.
      if (this.#isMobile()) {
        this.#initMobileScroll(originals);
        return;
      }

      this.#buildClones(originals);
      this.#activeAbs = this.#n; // first real card (start of the middle set)

      this.#wireArrows();
      this.#wireKeyboard();
      this.#wireScrubber();
      this.#wireResize();
      this.#wireDrag();
      this.#wireWheel();

      // Defer to the first frame so card widths are laid out before we measure.
      requestAnimationFrame(() => this.#render(true));
    }

    disconnectedCallback() {
      clearTimeout(this.#resetTimer);
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    // Matches the snippet's ≤749px breakpoint where the CSS turns the track into
    // a native scroll-snap container. Mode is fixed at connect (a breakpoint
    // cross needs a reload — same tradeoff as the PDP gallery).
    #isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    // Clone the whole set before and after the real cards so there's always a
    // full period on each side. Clones are inert for AT + keyboard (only the
    // real middle set is focusable / announced).
    #buildClones(originals) {
      // Drop any clones a previous mount left in place before re-cloning.
      this.#track.querySelectorAll('[data-clone]').forEach((c) => c.remove());
      const mk = (node) => {
        const c = node.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('tabindex', '-1');
        c.dataset.clone = '1';
        return c;
      };
      const before = originals.map(mk);
      const after  = originals.map(mk);
      const frag = document.createDocumentFragment();
      before.forEach((c) => frag.appendChild(c));
      this.#track.insertBefore(frag, originals[0]);
      after.forEach((c) => this.#track.appendChild(c));
      this.#cards = [...before, ...originals, ...after];
    }

    #realIndex() {
      return (((this.#activeAbs - this.#n) % this.#n) + this.#n) % this.#n;
    }

    // Centre #activeAbs and paint per-card scale/opacity by distance. When
    // `instant`, transitions are suspended (initial paint + invisible rebase).
    #render(instant) {
      const active = this.#cards[this.#activeAbs];
      if (!active) return;
      const snap = instant || this.#reduce;

      if (snap) {
        this.#track.classList.add('is-instant');
        this.#cards.forEach((c) => c.classList.add('is-instant'));
      }

      this.#paintAt(this.#activeAbs);

      if (snap) {
        // Commit the snapped layout, then re-enable transitions for the next move.
        void this.#track.offsetWidth;
        this.#track.classList.remove('is-instant');
        this.#cards.forEach((c) => c.classList.remove('is-instant'));
      }

      this.#paintControls();
    }

    // Position the strip so `center` (a fractional card index) sits dead-centre
    // and paint every card's scale/opacity by its continuous distance from
    // `center`. At integer `center` this reduces to the discrete coverflow steps
    // (0 → 1.06/1, 1 → .86/.6, 2 → .74/.28, ≥3 → .7/0), so arrow/keyboard/scrubber
    // output is unchanged; fractional values are interpolated so a live drag
    // follows the finger smoothly. offsetLeft/offsetWidth are layout metrics —
    // the inline scale() transforms don't perturb them, so centres stay stable.
    #paintAt(center) {
      const cards = this.#cards;
      if (!cards.length) return;

      const lo = Math.max(0, Math.min(cards.length - 1, Math.floor(center)));
      const hi = Math.min(cards.length - 1, lo + 1);
      const t = center - lo;
      const cenLo = cards[lo].offsetLeft + cards[lo].offsetWidth / 2;
      const cenHi = cards[hi].offsetLeft + cards[hi].offsetWidth / 2;
      const cen = cenLo + (cenHi - cenLo) * t;
      const x = Math.round(this.#viewport.clientWidth / 2 - cen);
      this.#track.style.transform = `translateX(${x}px)`;

      const activeI = Math.round(center);
      cards.forEach((card, i) => {
        const d = Math.abs(i - center);
        let scale;
        let opacity;
        if (d >= 3)      { scale = 0.7;                   opacity = 0; }
        else if (d >= 2) { scale = 0.74 - (d - 2) * 0.04; opacity = 0.28 * (3 - d); }
        else if (d >= 1) { scale = 0.86 - (d - 1) * 0.12; opacity = 0.6 - (d - 1) * 0.32; }
        else             { scale = 1.06 - d * 0.20;       opacity = 1 - d * 0.4; }
        card.style.transform = `scale(${scale})`;
        card.style.opacity = String(opacity);
        card.style.zIndex = String(100 - Math.round(d));
        card.style.pointerEvents = d > 2 ? 'none' : '';
        card.classList.toggle('is-active', i === activeI);
      });
    }

    #paintControls() {
      const real = this.#realIndex();
      if (this.#currentEl) this.#currentEl.textContent = String(real + 1);
      if (this.#n > 1) {
        const frac = real / (this.#n - 1);
        if (this.#fill)   this.#fill.style.width = `${frac * 100}%`;
        if (this.#handle) {
          this.#handle.style.left = `${frac * 100}%`;
          this.#handle.setAttribute('aria-valuenow', String(real + 1));
        }
      }
      if (this.#prevBtn) this.#prevBtn.setAttribute('aria-label', `Previous product (${real + 1} of ${this.#n})`);
      if (this.#nextBtn) this.#nextBtn.setAttribute('aria-label', `Next product (${real + 1} of ${this.#n})`);
    }

    // Slide one card. One move at a time so rapid clicks can't outrun the
    // clone buffer; the rebase after the slide keeps the index in the middle
    // period (invisible — clone content is identical to the real set).
    #go(dir) {
      if (this.#animating) return;
      this.#animating = true;
      this.#activeAbs += dir;
      this.#render(false);
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => {
        this.#normalize();
        this.#animating = false;
      }, this.#reduce ? 0 : EASE_MS + 20);
    }

    #normalize() {
      let changed = false;
      while (this.#activeAbs >= 2 * this.#n) { this.#activeAbs -= this.#n; changed = true; }
      while (this.#activeAbs < this.#n)       { this.#activeAbs += this.#n; changed = true; }
      if (changed) this.#render(true);
    }

    #wireArrows() {
      const onPrev = () => this.#go(-1);
      const onNext = () => this.#go(1);
      if (this.#prevBtn) this.#prevBtn.addEventListener('click', onPrev);
      if (this.#nextBtn) this.#nextBtn.addEventListener('click', onNext);
      this.#cleanups.push(() => {
        if (this.#prevBtn) this.#prevBtn.removeEventListener('click', onPrev);
        if (this.#nextBtn) this.#nextBtn.removeEventListener('click', onNext);
      });
    }

    #wireKeyboard() {
      const onKey = (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.#go(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.#go(1); }
      };
      this.addEventListener('keydown', onKey);
      this.#cleanups.push(() => this.removeEventListener('keydown', onKey));
    }

    // Scrubber jumps straight to a card (centred). It targets the middle set,
    // so it never needs a rebase.
    #wireScrubber() {
      if (!this.#scrubber || !this.#handle) return;

      const jump = (clientX) => {
        const rect = this.#scrubber.getBoundingClientRect();
        const f = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        this.#activeAbs = this.#n + Math.round(f * (this.#n - 1));
        this.#render(true);
      };

      let dragging = false;
      const onDown = (e) => {
        e.preventDefault();
        dragging = true;
        if (this.#scrubber.setPointerCapture) {
          try { this.#scrubber.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        }
        if (this.#handle.focus) this.#handle.focus();
        jump(e.clientX);
      };
      const onMove = (e) => { if (dragging) jump(e.clientX); };
      const onUp = (e) => {
        if (!dragging) return;
        dragging = false;
        if (this.#scrubber.releasePointerCapture) {
          try { this.#scrubber.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        }
      };
      const onKey = (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')  { e.preventDefault(); this.#go(-1); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp')   { e.preventDefault(); this.#go(1); }
      };

      this.#scrubber.addEventListener('pointerdown', onDown);
      this.#scrubber.addEventListener('pointermove', onMove);
      this.#scrubber.addEventListener('pointerup', onUp);
      this.#scrubber.addEventListener('pointercancel', onUp);
      this.#handle.addEventListener('keydown', onKey);

      this.#cleanups.push(() => {
        this.#scrubber.removeEventListener('pointerdown', onDown);
        this.#scrubber.removeEventListener('pointermove', onMove);
        this.#scrubber.removeEventListener('pointerup', onUp);
        this.#scrubber.removeEventListener('pointercancel', onUp);
        this.#handle.removeEventListener('keydown', onKey);
      });
    }

    // GA4: fire `product_siblings_click` via gtag on card clicks. GA4 events go
    // out over sendBeacon, so they survive the <a> navigation. Params are flat
    // (GA4 doesn't accept nested objects). Captures the current (from) product,
    // the clicked (to) product, and which part of the card was hit
    // ('image' | 'title' | 'card'). Delegated on the track so it also covers
    // the peeking clone cards.
    #wireTracking() {
      const onClick = (e) => {
        const card = e.target.closest('[data-mb-carousel-card]');
        if (!card) return;
        if (typeof window.gtag !== 'function') return;

        let clicked = 'card';
        if (e.target.closest('.mb-carousel__name')) clicked = 'title';
        else if (e.target.closest('.mb-carousel__frame')) clicked = 'image';

        window.gtag('event', 'product_siblings_click', {
          clicked_element: clicked,
          from_product_id: this.dataset.fromId || '',
          from_product_handle: this.dataset.fromHandle || '',
          from_product_title: this.dataset.fromTitle || '',
          to_product_id: card.dataset.productId || '',
          to_product_handle: card.dataset.productHandle || '',
          to_product_title: card.dataset.productTitle || '',
          to_product_url: card.getAttribute('href') || '',
        });
      };
      this.#track.addEventListener('click', onClick);
      this.#cleanups.push(() => this.#track.removeEventListener('click', onClick));
    }

    #wireResize() {
      let t = 0;
      const onResize = () => {
        clearTimeout(t);
        t = setTimeout(() => this.#render(true), 120);
      };
      window.addEventListener('resize', onResize, { passive: true });
      this.#cleanups.push(() => { clearTimeout(t); window.removeEventListener('resize', onResize); });
    }

    // Distance (px) between adjacent card centres — one "slide" of translate.
    #step() {
      if (this.#cards.length < 2) return this.#cards[0] ? this.#cards[0].offsetWidth : 1;
      return (this.#cards[1].offsetLeft - this.#cards[0].offsetLeft) || 1;
    }

    // Pointer drag / touch swipe → slide. The strip follows the finger live
    // (fractional centre via #paintAt); on release it snaps to the nearest card.
    // touch-action:pan-y (set in the snippet CSS) lets a vertical swipe scroll the
    // page while a horizontal swipe drives the carousel. A real drag (>6px) sets
    // #suppressClick so the trailing click never navigates to a product — a swipe
    // must not open a PDP by accident. A genuine tap (≤6px) clicks through.
    #wireDrag() {
      if (this.#n < 2 || !this.#viewport) return;
      const vp = this.#viewport;
      let dragging = false;
      let captured = false;
      let startX = 0;
      let startActive = 0;
      let startTs = 0;
      let moved = 0;
      let pid = null;

      const clampCenter = (c) => Math.max(1, Math.min(this.#cards.length - 2, c));

      // Promote a press to a real drag: capture the pointer and freeze
      // transitions. Deliberately NOT called from pointerdown — setting pointer
      // capture there retargets the trailing `click` to the viewport <div>
      // (the click lands on the common ancestor of mousedown/mouseup, and the
      // captured mouseup fires on the viewport), so a plain tap on a card would
      // lose its native <a> navigation. We only capture once the pointer crosses
      // the tap threshold, by which point the gesture is unambiguously a drag.
      const beginDrag = () => {
        captured = true;
        try { vp.setPointerCapture(pid); } catch { /* ignore */ }
        this.classList.add('is-grabbing');
        this.#track.classList.add('is-instant');
        this.#cards.forEach((c) => c.classList.add('is-instant'));
      };

      const onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (this.#animating) return;
        dragging = true;
        captured = false;
        moved = 0;
        startX = e.clientX;
        startActive = this.#activeAbs;
        startTs = e.timeStamp;
        pid = e.pointerId;
        this.#suppressClick = false;
      };

      const onMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > moved) moved = Math.abs(dx);
        if (!captured) {
          if (Math.abs(dx) <= 6) return; // still within tap slop — may be a click
          beginDrag();
        }
        this.#paintAt(clampCenter(startActive - dx / this.#step()));
      };

      const onUp = (e) => {
        if (!dragging) return;
        dragging = false;

        // Never crossed the tap threshold → a plain click. Leave #suppressClick
        // false so the card <a> navigates; there's nothing to snap.
        if (!captured) return;

        try { vp.releasePointerCapture(pid); } catch { /* ignore */ }
        this.classList.remove('is-grabbing');
        this.#track.classList.remove('is-instant');
        this.#cards.forEach((c) => c.classList.remove('is-instant'));

        this.#suppressClick = true; // a real drag must not open a PDP by accident

        // Snap decision. A short flick or even a ~quarter-card drag advances one
        // card; a decisive (≥half) drag snaps to the nearest card (may be >1).
        // Direction is derived from the sign so backward moves aren't lost to
        // Math.round(-0.5) === 0. Velocity (px/ms) lets a fast, short swipe count.
        const dx = e.clientX - startX;
        const s = this.#step();
        const dt = e.timeStamp - startTs;
        const vel = dt > 0 ? Math.abs(dx) / dt : 0;
        const dir = dx < 0 ? 1 : -1; // drag left → advance forward
        const mag = Math.abs(dx) / s; // travel in cards
        let steps = 0;
        if (mag >= 0.5) {
          steps = dir * Math.round(mag);
        } else if (Math.abs(dx) > 8 && (vel > 0.35 || mag >= 0.28)) {
          steps = dir;
        }

        if (steps !== 0) {
          this.#animating = true;
          this.#activeAbs = startActive + steps;
          this.#render(false);
          clearTimeout(this.#resetTimer);
          this.#resetTimer = setTimeout(() => {
            this.#normalize();
            this.#animating = false;
          }, this.#reduce ? 0 : EASE_MS + 20);
        } else {
          this.#render(false); // no net move — ease back to where we started
        }
      };

      // Capture-phase: a drag-suppressed click is killed before it reaches the
      // card <a> (navigation) or the #wireTracking gtag handler on the track.
      const onClick = (e) => {
        if (!this.#suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
        this.#suppressClick = false;
      };

      vp.addEventListener('pointerdown', onDown);
      vp.addEventListener('pointermove', onMove);
      vp.addEventListener('pointerup', onUp);
      vp.addEventListener('pointercancel', onUp);
      vp.addEventListener('click', onClick, true);

      this.#cleanups.push(() => {
        vp.removeEventListener('pointerdown', onDown);
        vp.removeEventListener('pointermove', onMove);
        vp.removeEventListener('pointerup', onUp);
        vp.removeEventListener('pointercancel', onUp);
        vp.removeEventListener('click', onClick, true);
      });
    }

    // Trackpad horizontal swipe (and horizontal mouse wheel) → step one card.
    // Only horizontal-dominant deltas are consumed; vertical scroll passes
    // through to the page untouched. Deltas accumulate to a threshold so a flick's
    // momentum doesn't fly through the whole strip, and a >200ms gap resets the
    // accumulator between separate gestures.
    #wireWheel() {
      if (this.#n < 2 || !this.#viewport) return;
      const THRESH = 40;
      let accum = 0;
      let lastTs = 0;
      const onWheel = (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical → page
        e.preventDefault();
        if (this.#animating) return;
        const now = e.timeStamp || 0;
        if (now - lastTs > 200) accum = 0;
        lastTs = now;
        accum += e.deltaX;
        if (Math.abs(accum) >= THRESH) {
          this.#go(accum > 0 ? 1 : -1);
          accum = 0;
        }
      };
      this.#viewport.addEventListener('wheel', onWheel, { passive: false });
      this.#cleanups.push(() => this.#viewport.removeEventListener('wheel', onWheel));
    }

    // Mobile: native horizontal scroll-snap strip — the browser handles swipe,
    // momentum, and (crucially) vertical-vs-horizontal intent, so a page scroll
    // never moves the carousel. Finite (no clones/loop). A passive scroll
    // listener syncs the counter / scrubber / active card off scrollLeft; arrows
    // scrollBy one card; the scrubber maps a fraction onto the scroll range.
    #initMobileScroll(originals) {
      this.#cards = originals;
      const track = this.#track;
      const stepOf = () => {
        if (originals.length < 2) return originals[0] ? originals[0].offsetWidth : 1;
        return (originals[1].offsetLeft - originals[0].offsetLeft) || 1;
      };

      const sync = () => {
        const i = Math.max(0, Math.min(this.#n - 1, Math.round(track.scrollLeft / stepOf())));
        originals.forEach((c, k) => c.classList.toggle('is-active', k === i));
        if (this.#currentEl) this.#currentEl.textContent = String(i + 1);
        const frac = this.#n > 1 ? i / (this.#n - 1) : 0;
        if (this.#fill) this.#fill.style.width = `${frac * 100}%`;
        if (this.#handle) {
          this.#handle.style.left = `${frac * 100}%`;
          this.#handle.setAttribute('aria-valuenow', String(i + 1));
        }
        if (this.#prevBtn) this.#prevBtn.setAttribute('aria-label', `Previous product (${i + 1} of ${this.#n})`);
        if (this.#nextBtn) this.#nextBtn.setAttribute('aria-label', `Next product (${i + 1} of ${this.#n})`);
      };

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; sync(); });
      };
      track.addEventListener('scroll', onScroll, { passive: true });
      this.#cleanups.push(() => track.removeEventListener('scroll', onScroll));

      const behavior = this.#reduce ? 'auto' : 'smooth';
      const go = (dir) => track.scrollBy({ left: dir * stepOf(), behavior });
      const onPrev = () => go(-1);
      const onNext = () => go(1);
      if (this.#prevBtn) this.#prevBtn.addEventListener('click', onPrev);
      if (this.#nextBtn) this.#nextBtn.addEventListener('click', onNext);
      this.#cleanups.push(() => {
        if (this.#prevBtn) this.#prevBtn.removeEventListener('click', onPrev);
        if (this.#nextBtn) this.#nextBtn.removeEventListener('click', onNext);
      });

      if (this.#scrubber && this.#handle) {
        const jump = (clientX) => {
          const rect = this.#scrubber.getBoundingClientRect();
          const f = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          track.scrollTo({ left: f * (track.scrollWidth - track.clientWidth), behavior: 'auto' });
        };
        let dragging = false;
        const onDown = (e) => {
          e.preventDefault();
          dragging = true;
          try { this.#scrubber.setPointerCapture(e.pointerId); } catch { /* ignore */ }
          jump(e.clientX);
        };
        const onMove = (e) => { if (dragging) jump(e.clientX); };
        const onUp = (e) => {
          dragging = false;
          try { this.#scrubber.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        };
        const onKey = (e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')  { e.preventDefault(); go(-1); }
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp')   { e.preventDefault(); go(1); }
        };
        this.#scrubber.addEventListener('pointerdown', onDown);
        this.#scrubber.addEventListener('pointermove', onMove);
        this.#scrubber.addEventListener('pointerup', onUp);
        this.#scrubber.addEventListener('pointercancel', onUp);
        this.#handle.addEventListener('keydown', onKey);
        this.#cleanups.push(() => {
          this.#scrubber.removeEventListener('pointerdown', onDown);
          this.#scrubber.removeEventListener('pointermove', onMove);
          this.#scrubber.removeEventListener('pointerup', onUp);
          this.#scrubber.removeEventListener('pointercancel', onUp);
          this.#handle.removeEventListener('keydown', onKey);
        });
      }

      requestAnimationFrame(sync);
    }
  }

  customElements.define('mb-product-carousel', MBProductCarousel);
}
